import React, { useState } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Provider } from 'react-redux';
import { AuthenticationResult, EventType, IPublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { getRouteForUserPortalRole } from '@/root-app/utils/authenticatedApp.utils';
import { theme } from '@/core/theme/theme';
import RootLoadingPage from '@/global-pages/RootLoadingPage';
import { globalStyles } from '@/core/globalStyles';
import store from '@/core/store/store';
import { AlertProvider } from '@/core/providers/AlertContext';
import { UnsavedConfirmationProvider } from '@/core/providers/UnsavedConfirmationContext';
import { getUserPortalRole, getUserAgreementState } from '@/core/web-storage/webStorage.utils';
import { isEmployerUser } from '@/authorization/utils/authorization.utils';
import webStorage, { LOCAL_STORAGE_KEYS } from '@/core/web-storage/webStorage';
import { TimeoutProvider } from '@/core/providers/TimeoutProvider';
import { SystemAlertProvider } from '@/core/providers/SystemAlertContext';
import {
  getCreateBrowserRouterFutureFlags,
  getRouterProviderFutureFlags,
} from '@/common/utils/app.utils';

interface AppProvidersProps {
  pca: IPublicClientApplication;
}

function AppProviders({ pca }: AppProvidersProps) {
  const [router, setRouter] = useState(
    createBrowserRouter(getRouteForUserPortalRole(getUserPortalRole(), getUserAgreementState()), {
      future: getCreateBrowserRouterFutureFlags(),
    }),
  );
  const styleCache = createCache({
    key: 'emotion-cache-key',
    nonce: CSP_NONCE,
  });

  pca.addEventCallback((event) => {
    if (event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS && event.payload) {
      const authenticationResult = event.payload as AuthenticationResult;
      const accessToken = authenticationResult?.accessToken;
      if (accessToken) {
        const userPortalRole = getUserPortalRole();
        const isUserAgreementAccepted = getUserAgreementState();
        if (isUserAgreementAccepted === null) {
          setRouter(
            createBrowserRouter(
              getRouteForUserPortalRole(userPortalRole, isUserAgreementAccepted),
              {
                future: getCreateBrowserRouterFutureFlags(),
              },
            ),
          );
          if (!isEmployerUser(userPortalRole)) {
            webStorage.local.setItem(LOCAL_STORAGE_KEYS.USER_AGREEMENT_STATE, 'true');
            window.location.reload();
          }
        }
      }
    }
  });

  return (
    <CacheProvider value={styleCache}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {globalStyles}
        <MsalProvider instance={pca}>
          <Provider store={store}>
            <TimeoutProvider>
              <SystemAlertProvider>
                <AlertProvider>
                  <UnsavedConfirmationProvider>
                    <React.Suspense fallback={<RootLoadingPage />}>
                      <RouterProvider router={router} future={getRouterProviderFutureFlags()} />
                    </React.Suspense>
                  </UnsavedConfirmationProvider>
                </AlertProvider>
              </SystemAlertProvider>
            </TimeoutProvider>
          </Provider>
        </MsalProvider>
      </ThemeProvider>
    </CacheProvider>
  );
}

export default AppProviders;
